// ═══════════════════════════════════════════════════════
// TraceFull v5 Frontend — API Client
//
// All calls go through /api/* which Vite proxies to
// localhost:3001 in dev, and Nginx/load-balancer in prod.
// Access token injected from localStorage on every request.
// On 401, attempts token refresh then retries once.
// ═══════════════════════════════════════════════════════

const BASE = (import.meta.env.VITE_API_URL as string) || '/api';

// ── Token management ─────────────────────────────────
export function getToken(): string | null {
  return localStorage.getItem('tf_access_token');
}
export function setToken(t: string): void {
  localStorage.setItem('tf_access_token', t);
}
export function clearToken(): void {
  localStorage.removeItem('tf_access_token');
}

// ── Core fetch wrapper ────────────────────────────────
async function apiFetch<T>(
  path: string,
  options: RequestInit & { skipContentType?: boolean } = {},
  retry = true,
): Promise<T> {
  const token = getToken();
  const { skipContentType, ...fetchOptions } = options;
  const headers: Record<string, string> = {
    ...(skipContentType ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOptions.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...fetchOptions, headers });

  // Token expired — try refresh once
  if (res.status === 401 && retry) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiFetch<T>(path, options, false);
    clearToken();
    window.dispatchEvent(new Event('piq:logout'));
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// Shared refresh promise — all concurrent 401s await the same call
// instead of each firing their own refresh (which exhausts the rate limit).
let _refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = await res.json();
      setToken(data.access_token);
      return true;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// ── Auth ──────────────────────────────────────────────
export const auth = {
  login: (email: string, password: string) =>
    apiFetch<{ access_token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (data: { company_name: string; email: string; password: string; display_name: string }) =>
    apiFetch<{ access_token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  logout: () =>
    apiFetch<void>('/auth/logout', { method: 'POST' }).catch(() => {}),

  me: () => apiFetch<User>('/auth/me'),
};

// ── Billing ───────────────────────────────────────────
export const billingApi = {
  status: () => apiFetch<{
    tier: string; subscription_status: string;
    trial_ends_at: string | null; current_period_end: string | null;
    has_payment_method: boolean; dev_mode: boolean;
    limits: {
      products:  { used: number; max: number };
      users:     { used: number; max: number };
      api_calls: { used: number; max: number };
    };
    tiers: Record<string, { price_usd: number | null; max_products: number; max_users: number }>;
  }>('/billing/status'),

  createCheckoutSession: (tier: string) =>
    apiFetch<{ url: string }>('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    }),

  createPortalSession: () =>
    apiFetch<{ url: string }>('/billing/create-portal-session', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Dev only
  devActivateTier: (tier: string) =>
    apiFetch<{ message: string }>('/billing/dev/activate-tier', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    }),
};

// ── Onboarding ────────────────────────────────────────
export const onboardingApi = {
  status: () => apiFetch<{
    current_step: number; completed: boolean;
    steps: { step: number; title: string; completed: boolean }[];
    tenant: { name: string; logo_url: string | null; industry: string | null; country: string | null };
  }>('/onboarding/status'),
  completeStep: (step: number, body?: Record<string, unknown>) =>
    apiFetch<{ message: string; next_step?: number }>(`/onboarding/step/${step}`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
};

// ── Users ─────────────────────────────────────────────
export const usersApi = {
  list: () => apiFetch<Array<{
    id: string; email: string; display_name: string; role: string;
    is_active: boolean; last_login_at: string | null; invited_at: string;
  }>>('/users'),
  invite: (data: { email: string; display_name: string; role: string }) =>
    apiFetch<{ message: string; invite_url: string; user: { id: string; email: string; role: string } }>('/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateRole: (userId: string, role: string) =>
    apiFetch<{ message: string }>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  deactivate: (userId: string) =>
    apiFetch<{ message: string }>(`/users/${userId}`, { method: 'DELETE' }),
};

// ── Import ────────────────────────────────────────────
export const importApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch<{
      job_id: string; filename: string; total_rows: number;
      headers: string[]; column_mapping: Record<string, string>;
      preview_rows: Record<string, string>[];
      unmapped_columns: string[];
    }>('/import/upload', { method: 'POST', body: form, skipContentType: true });
  },
  confirm: (jobId: string, columnMapping: Record<string, string>) =>
    apiFetch<{
      job_id: string; total_rows: number; valid_rows: number;
      error_rows: number; errors: { row: number; field: string; message: string }[];
      ready_to_import: boolean;
    }>(`/import/${jobId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ column_mapping: columnMapping }),
    }),
  run: (jobId: string) =>
    apiFetch<{ message: string; imported: number; skipped: number; rollback_available: boolean }>(
      `/import/${jobId}/run`, { method: 'POST', body: JSON.stringify({}) }
    ),
  rollback: (jobId: string) =>
    apiFetch<{ message: string; removed: number }>(`/import/${jobId}/rollback`, { method: 'POST', body: JSON.stringify({}) }),
  list: () =>
    apiFetch<Array<{
      id: string; filename: string; status: string; total_rows: number;
      imported_rows: number; error_rows: number; rollback_available: boolean;
      rolled_back_at: string | null; completed_at: string | null; created_at: string;
      created_by_name: string;
    }>>('/import'),
  get: (jobId: string) => apiFetch<Record<string, unknown>>(`/import/${jobId}`),
};

// ── Webhooks ──────────────────────────────────────────
export const webhooksApi = {
  list: () => apiFetch<Array<{
    id: string; url: string; description: string | null; events: string[];
    is_active: boolean; failure_count: number; last_success_at: string | null;
    disabled_at: string | null; created_at: string;
  }>>('/webhooks'),
  create: (data: { url: string; events: string[]; description?: string }) =>
    apiFetch<{ id: string; url: string; events: string[]; secret: string; message: string }>('/webhooks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<{ events: string[]; is_active: boolean; description: string }>) =>
    apiFetch<{ id: string }>(`/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch<{ message: string }>(`/webhooks/${id}`, { method: 'DELETE' }),
  test: (id: string) =>
    apiFetch<{ success: boolean; status_code: number | null; duration_ms: number }>(`/webhooks/${id}/test`, {
      method: 'POST', body: JSON.stringify({}),
    }),
  deliveries: (id: string) => apiFetch<Array<{
    id: string; event_type: string; attempt: number; status_code: number | null;
    success: boolean; duration_ms: number; error_message: string | null; delivered_at: string;
  }>>(`/webhooks/${id}/deliveries`),
  redeliver: (deliveryId: string) =>
    apiFetch<{ message: string }>(`/webhooks/deliveries/${deliveryId}/redeliver`, { method: 'POST', body: JSON.stringify({}) }),
  events: () => apiFetch<{ events: string[] }>('/webhooks/events'),
};

// ── Products ──────────────────────────────────────────
export const productsApi = {
  list: (params?: { retailer?: string; risk?: string; category?: string; limit?: number }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<{ products: Product[]; total: number }>(`/products${q ? '?' + q : ''}`);
  },
  get: (id: string) => apiFetch<Product>(`/products/${id}`),
  chain: (id: string) => apiFetch<ProvenanceNode[]>(`/products/${id}/chain`),
};

// ── Alerts ────────────────────────────────────────────
export const alertsApi = {
  list: (params?: { status?: string; severity?: string; limit?: number }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<{ alerts: Alert[]; total: number; unread: number }>(`/alerts${q ? '?' + q : ''}`);
  },
  get: (id: string) => apiFetch<Alert>(`/alerts/${id}`),
  update: (id: string, body: Partial<Pick<Alert, 'status'>> & { action_taken?: string }) =>
    apiFetch<{ message: string; alert: Alert }>(`/alerts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  markRead: (id: string) =>
    apiFetch<{ message: string }>(`/alerts/${id}/mark-read`, { method: 'POST' }),
  recommend: (id: string) =>
    apiFetch<{ recommendation: string; cached: boolean }>(`/alerts/${id}/recommend`, { method: 'POST' }),
};

// ── API Keys ──────────────────────────────────────────
export const apiKeysApi = {
  list: () => apiFetch<{ keys: ApiKey[] }>('/keys'),
  create: (body: { name: string; env: string; scopes: string[] }) =>
    apiFetch<{ key: ApiKey; full_key: string }>('/keys', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revoke: (id: string) =>
    apiFetch<{ message: string }>(`/keys/${id}/revoke`, { method: 'POST' }),
};

// ── Retailers ─────────────────────────────────────────
export const retailersApi = {
  list: () => apiFetch<Retailer[]>('/retailers'),
  get: (slug: string) => apiFetch<Retailer>(`/retailers/${slug}`),
};

// ── Suppliers ─────────────────────────────────────────
export const suppliersApi = {
  list: (params?: { country?: string; risk?: string; verified?: boolean }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<Supplier[]>(`/suppliers${q ? '?' + q : ''}`);
  },
};

// ── Agents ────────────────────────────────────────────
export const agentsApi = {
  list: () => apiFetch<AgentState[]>('/agents'),
  trigger: (agentType: string) =>
    apiFetch<{ message: string; jobId: string }>(`/agents/${agentType}/trigger`, { method: 'POST' }),
  queues: () => apiFetch<Record<string, QueueStats>>('/agents/queues'),
};

// ── AI ────────────────────────────────────────────────
export const ai = {
  chat: (messages: { role: 'user' | 'assistant'; content: string }[]) =>
    apiFetch<{ response: string; mode: 'live' | 'mock' }>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages }),
    }),
  report: (topic: string) =>
    apiFetch<{ content: string; mode: string }>('/ai/report', {
      method: 'POST',
      body: JSON.stringify({ topic }),
    }),
};

// ── Shared Types (mirrors backend types/index.ts) ────
export interface User {
  id: string; email: string; display_name: string; role: string;
  mfa_enabled: boolean; last_login_at: string | null; created_at: string;
  // v7 tenant fields
  tenant_id: string | null;
  tier: 'starter' | 'growth' | 'enterprise' | null;
  tenant_name: string | null;
  tenant_slug: string | null;
  features: string[];
}
export interface Product {
  id: string; retailer_id: string; retailer_name?: string; retailer_slug?: string;
  name: string; brand: string; category: string; origin_description: string;
  price_usd: number; img_emoji: string; provenance_score: number;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'; verified: boolean;
  flag_count: number; blockchain_hash: string | null; updated_at: string;
  certifications?: string[];
  score_breakdown?: {
    origin?: number; ethics?: number; purity?: number;
    geographic_origin?: number; supplier_verification?: number;
    chain_of_custody?: number; labor_standards?: number;
    environmental_impact?: number; certifications?: number;
    contaminant_testing?: number; allergen_accuracy?: number;
    blockchain_verification?: number;
    chain_stages?: Array<{
      stage: string; location: string; date: string;
      status: string; icon: string; detail: string;
    }>;
  } | null;
}
export interface ProvenanceNode {
  id: string; stage: string; label: string; detail: string;
  verified: boolean; verification_status: string; stage_score: number; stage_date: string | null;
}
export interface Alert {
  id: string; severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  title: string; detail: string; product_id: string | null; product_name?: string;
  agent_id: string | null; read: boolean; created_at: string; resolved_at: string | null;
}
export interface ApiKey {
  id: string; name: string; key_prefix: string; environment: string;
  status: string; scopes: string[]; call_count: number; call_limit: number;
  last_used_at: string | null; expires_at: string | null;
  created_at: string; last_used_relative: string;
}
export interface Retailer {
  id: string; slug: string; name: string; logo_emoji: string;
  status: string; product_count: number; color_hex: string;
  avg_score?: number; verified_count?: number; high_risk_count?: number;
}
export interface Supplier {
  id: string; name: string; country: string; region: string;
  supplier_type: string; latitude: number; longitude: number;
  provenance_score: number; verified: boolean; risk_level: string;
}
export interface AgentState {
  id: string; name: string; agent_type: string; status: string;
  current_task: string | null; confidence: number; actions_today: number; uptime_pct: number;
}
export interface QueueStats {
  waiting: number; active: number; completed: number; failed: number;
}

// ── Tenants ───────────────────────────────────────────────────────
// v7.14.0 (Sequencing #9 / Bundle B): per-tenant recall reviewer email.
// `me` re-uses the existing /api/tenants/me endpoint (returns t.*); the
// reviewer email field is just one of many columns on that row.
export interface TenantMe {
  id: string;
  name: string;
  slug: string;
  tier: string;
  recall_reviewer_email: string | null;
  [key: string]: unknown;
}
export const tenantsApi = {
  me: () => apiFetch<TenantMe>('/tenants/me'),
  updateRecallReviewerEmail: (recall_reviewer_email: string | null) =>
    apiFetch<{ id: string; recall_reviewer_email: string | null }>(
      '/tenants/me/recall-reviewer-email',
      { method: 'PATCH', body: JSON.stringify({ recall_reviewer_email }) },
    ),
};

// ── Transparency Requests (v7.18.0 / Sequencing #13 / Bundle T) ──────────────
// Tenant-side triage surface for the v7.10.0 V-A consumer intake. Role-gated
// at the backend (admin / super_admin); the page should not be exposed in
// the sidebar for non-admins (route still returns 403 if reached).
export interface TransparencyRequest {
  id:               string;
  tenant_id:        string | null;
  product_id:       string;
  product_name:     string | null;
  question_text:    string;
  requester_email:  string | null;
  request_type:     string;
  status:           'pending' | 'in_progress' | 'responded' | 'closed';
  response_text:    string | null;
  responded_by:     string | null;
  responder_name?:  string | null;
  responded_at:     string | null;
  created_at:       string;
  updated_at:       string;
}
export const transparencyRequestsApi = {
  list: (params?: { status?: string; product_id?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<{ requests: TransparencyRequest[]; total: number; limit: number; offset: number }>(
      `/transparency-requests${q ? '?' + q : ''}`,
    );
  },
  get: (id: string) => apiFetch<TransparencyRequest>(`/transparency-requests/${id}`),
  patch: (id: string, body: { status?: string; response_text?: string }) =>
    apiFetch<TransparencyRequest>(`/transparency-requests/${id}`, {
      method: 'PATCH',
      body:   JSON.stringify(body),
    }),
};

// ── Recalls ───────────────────────────────────────────────────────
export const recallsApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<{ recalls: RecallEvent[] }>(`/recalls${q}`);
  },
  get: (id: string) => apiFetch<RecallEvent & { affected_products: RecallAffectedProduct[] }>(`/recalls/${id}`),
  initiate: (body: {
    supplierId?: string; productId?: string; lotNumber?: string;
    hazardType: string; pathogen?: string; description: string;
    recallType: 'voluntary' | 'mandatory' | 'market_withdrawal';
  }) => apiFetch<RecallScope>('/recalls', { method: 'POST', body: JSON.stringify(body) }),
  close: (id: string) => apiFetch<{ message: string }>(`/recalls/${id}/close`, { method: 'POST' }),
  updateProduct: (recallId: string, productId: string, body: { status: string }) =>
    apiFetch<{ message: string }>(`/recalls/${recallId}/products/${productId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  searchLots: (params: { lot_number?: string; product_id?: string; supplier_id?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<{ lots: ProductLot[] }>(`/recalls/lots/search?${q}`);
  },
};

// ── DPP ───────────────────────────────────────────────────────────
export const dppApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<{ dpps: DppRecord[] }>(`/dpp${q}`);
  },
  get: (productId: string) => apiFetch<DppRecord>(`/dpp/${productId}`),
  generate: (productId: string) => apiFetch<DppRecord>(`/dpp/${productId}/generate`, { method: 'POST' }),
  publish: (productId: string) => apiFetch<{ message: string; dppId: string }>(`/dpp/${productId}/publish`, { method: 'POST' }),
};

// ── IoT ───────────────────────────────────────────────────────────
export const iotApi = {
  readings: (params?: { device_id?: string; product_id?: string; alert_only?: boolean; limit?: number }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return apiFetch<{ readings: SensorReading[] }>(`/iot/readings${q ? '?' + q : ''}`);
  },
  devices: () => apiFetch<{ devices: IoTDevice[] }>('/iot/devices'),
};

// ── Supplier Portal ───────────────────────────────────────────────
export interface SupplierSubmission {
  id: string; supplier_id: string; tenant_id: string;
  submission_type: string; status: 'pending' | 'approved' | 'rejected';
  payload: Record<string, unknown>; reviewer_notes: string | null;
  created_at: string; reviewed_at: string | null;
  supplier_name?: string; display_name?: string;
}

export const portalApi = {
  login: (email: string, password: string) =>
    apiFetch<{ access_token: string; user: { id: string; display_name: string; supplier_id: string } }>('/portal/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  submissions: {
    list: () => apiFetch<{ submissions: SupplierSubmission[] }>('/portal/submissions'),
    create: (submissionType: string, payload: unknown) =>
      apiFetch<{ submission: SupplierSubmission }>('/portal/submissions', { method: 'POST', body: JSON.stringify({ submissionType, payload }) }),
  },
  review: {
    list: (status?: string) => {
      const q = status ? `?status=${status}` : '';
      return apiFetch<{ submissions: SupplierSubmission[] }>(`/portal/review${q}`);
    },
    decide: (id: string, status: string, reviewerNotes?: string) =>
      apiFetch<{ message: string }>(`/portal/review/${id}`, { method: 'PATCH', body: JSON.stringify({ status, reviewerNotes }) }),
  },
};

// ── v6 Shared Types ───────────────────────────────────────────────
export interface ProductLot {
  id: string; product_id: string; supplier_id: string | null; lot_number: string;
  quantity: number | null; unit: string; harvest_date: string | null;
  origin_location: string | null; status: string;
  product_name?: string; supplier_name?: string; retailer_name?: string;
}
export interface RecallEvent {
  id: string; title: string; description: string | null; recall_type: string;
  hazard_type: string; pathogen: string | null; status: string;
  scope_summary: string | null; affected_units: number | null;
  affected_retailers: string[]; initiated_at: string; closed_at: string | null;
}
export interface RecallAffectedProduct {
  id: string; recall_id: string; product_id: string; lot_id: string | null;
  retailer_id: string | null; lot_number: string | null; quantity: number | null;
  status: string; notified_at: string | null; removed_at: string | null;
  product_name?: string; img_emoji?: string; retailer_name?: string; retailer_slug?: string;
}
export interface RecallScope {
  recallId: string; affectedProducts: RecallAffectedProduct[];
  affectedRetailers: string[]; totalUnits: number;
  scopeSummary: string; traceTimeMs: number;
}
export interface DppRecord {
  id: string; product_id: string; dpp_id: string | null; schema_version: string;
  status: string; manufacturer_name: string | null; product_category: string | null;
  origin_country: string | null; recyclability_pct: number | null;
  eudr_compliant: boolean | null; lot_numbers: string[]; blockchain_hash: string | null;
  published_at: string | null; updated_at: string;
  carbon_footprint_kg?: number | null;
  product_name?: string; img_emoji?: string; retailer_name?: string; provenance_score?: number;
  certifications?: { name: string; issuing_body: string; expires_at: string | null; is_valid: boolean }[];
}
export interface SensorReading {
  id: string; device_id: string; device_type: string; value: number; unit: string;
  product_id: string | null; lot_id: string | null; location_label: string | null;
  alert_triggered: boolean; threshold_min: number | null; threshold_max: number | null;
  recorded_at: string; product_name?: string; lot_number?: string;
}
export interface IoTDevice {
  device_id: string; device_type: string; unit: string;
  last_seen: string; total_readings: number; violations: number;
}

// ── Analytics (Phase 4) ───────────────────────────────────────────────────────
export const analyticsApi = {
  getScoreTrend: (productId: string, days: 30 | 90 | 180 = 90) =>
    apiFetch<{ product: Product; trend: { date: string; score: number; trigger_type: string; delta: number }[]; change_30d: number | null; days: number }>(
      `/analytics/score/${productId}?days=${days}`
    ),
  getRetailerTrend: (retailerId: string, days: 30 | 90 | 180 = 90) =>
    apiFetch<{ retailer: { name: string; slug: string }; trend: { date: string; avg_score: number; product_count: number }[]; days: number }>(
      `/analytics/retailer/${retailerId}/trend?days=${days}`
    ),
  getSupplierLeague: (params?: { risk_tier?: string; sort_by?: string }) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch<{ suppliers: SupplierStat[]; refreshed_at: string | null; total: number }>(
      `/analytics/suppliers/league${q ? '?' + q : ''}`
    );
  },
  getAlertPattern: () =>
    apiFetch<AlertPattern & { available: boolean }>('/analytics/alerts/pattern'),
  generateAlertPattern: () =>
    apiFetch<{ message: string }>('/analytics/alerts/pattern/generate', { method: 'POST' }),
};

// ── Reports (Phase 4) ─────────────────────────────────────────────────────────
export const reportsApi = {
  create: (body: { report_type: string; title?: string; scope?: Record<string, unknown> }) =>
    apiFetch<{ job_id: string; report_type: string; title: string; status: string; message: string }>(
      '/reports', { method: 'POST', body: JSON.stringify(body) }
    ),
  list: () =>
    apiFetch<{ reports: ReportJob[] }>('/reports'),
  get: (id: string) =>
    apiFetch<ReportJob & { download_url: string | null }>(`/reports/${id}`),
  delete: (id: string) =>
    apiFetch<{ message: string }>(`/reports/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: string) => `/api/reports/${id}/download`,
};

// ── Phase 4 Types ─────────────────────────────────────────────────────────────
export interface SupplierStat {
  rank: number; supplier_id: string; name: string;
  country: string | null; supplier_type: string | null; verified: boolean;
  avg_score: number; product_count: number; active_alert_count: number;
  total_alert_count: number; recall_count: number; cert_coverage_pct: number;
  risk_level: string; updated_at: string;
}
export interface AlertPattern {
  id: string; tenant_id: string; week_start: string; week_end: string;
  total_alerts: number; critical_count: number; high_count: number;
  resolved_pct: number; top_suppliers: { supplier_id: string; supplier_name: string; alert_count: number; top_type: string }[];
  top_alert_types: { type: string; count: number }[];
  narrative: string | null; recommendations: { priority: string; text: string }[];
  ai_generated: boolean; generated_at: string | null;
}
export interface ReportJob {
  id: string; report_type: string; title: string; status: string;
  progress_pct: number; file_size_bytes: number | null; page_count: number | null;
  error_message: string | null; queued_at: string; started_at: string | null;
  completed_at: string | null; expires_at: string; created_by_name: string | null;
}

// ── Phase 5 — Agent Intelligence ─────────────────────────────────────────────
export const agentIntelApi = {
  getMemoryStats: () =>
    apiFetch<{ total: number; by_agent: { agent_type: string; count: number; avg_importance: number }[]; by_type: { memory_type: string; count: number }[] }>('/agent-intel/memory'),
  getAgentMemories: (agentType: string, params?: { limit?: number; memory_type?: string }) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch<{ memories: AgentMemory[]; total: number }>(`/agent-intel/memory/${agentType}${q ? '?' + q : ''}`);
  },
  deleteMemory: (id: string) => apiFetch<{ message: string }>(`/agent-intel/memory/${id}`, { method: 'DELETE' }),
  getMessageLog: (params?: { agent?: string; limit?: number }) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch<{ messages: AgentMessage[]; total: number }>(`/agent-intel/messages${q ? '?' + q : ''}`);
  },
  getScoringQueue: () =>
    apiFetch<{ stats: ScoringQueueStats; recent_events: ScoringEvent[] }>('/agent-intel/scoring-queue'),
  triggerRescore: (supplier_id: string, detail?: string) =>
    apiFetch<{ enqueued: number; message: string }>('/agent-intel/scoring-queue/trigger', { method: 'POST', body: JSON.stringify({ supplier_id, detail }) }),
  getRouterStats: (days?: number) =>
    apiFetch<RouterStats>(`/agent-intel/router-stats${days ? '?days=' + days : ''}`),
  getSchemas: () =>
    apiFetch<{ schemas: AgentSchema[] }>('/agent-intel/schemas'),
};

export interface AgentMemory {
  id: string; agent_type: string; memory_type: string; summary: string;
  entity_type: string | null; entity_id: string | null;
  importance: number; recall_count: number; confidence: number | null;
  created_at: string; expires_at: string | null;
}
export interface AgentMessage {
  id: string; from_agent: string; to_agent: string; message_type: string;
  subject: string; status: string; created_at: string; has_response: boolean;
}
export interface ScoringQueueStats {
  pending: number; processing: number; scored_today: number; failed: number; avg_priority: number;
}
export interface ScoringEvent {
  id: string; trigger_type: string; trigger_entity_type: string; priority: number;
  status: string; score_before: number | null; score_after: number | null;
  created_at: string; processed_at: string | null; product_name: string;
}
export interface RouterStats {
  total_calls: number; total_cost_usd: number;
  by_tier: { tier: string; calls: number; cost_usd: number; pct: number }[];
  by_task: { task_type: string; calls: number; avg_cost_usd: number }[];
  daily: { date: string; haiku: number; sonnet: number; opus: number; total_cost: number }[];
}
export interface AgentSchema {
  agent_type: string; task_type: string; tool_name: string;
  schema_version: string; is_active: boolean; success_rate: number | null; activated_at: string;
}
